/**
 * KAN-810: data and state of the page "Лиды по теплоте" (filters, page,
 * selection, bulk actions, export) and the manual level in the client card.
 * Components only render what these hooks return.
 * KAN-883: filter "Появился" (?lead_from=&lead_to=) in useLeadDayPeriod.
 * KAN-909: manager filter defaults to the user's own manager record (useManagerFilterChoice).
 */
import { useCallback, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { usePageSize } from '@/hooks/usePageSize';
import { useDebounce } from '@/hooks/useDebounce';
import { useLeadDayPeriod, type LeadDayState } from '@/hooks/useLeadDayPeriod';
import { useDropUnknownManager, useManagerFilterChoice } from '@/hooks/useManagerFilterChoice';
import { useManagers } from '@/hooks/useManagers';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { LEAD_TEMPERATURE_LEVELS, type LeadTemperatureLevel } from '@/lib/leadTemperature';
import type { ManagerChoiceValues } from '@/lib/managerFilterChoice';
import { clientsApi } from '@/services/api/clients';
import { clientTemperatureChangedKeys, invalidateKeys } from '@/services/api/clientEditKeys';
import {
  bulkLeadTemperature,
  exportLeadTemperature,
  fetchLeadTemperatureList,
} from '@/services/api/leadTemperature';
import {
  ATTENTION_QUERY_KEY,
  LEAD_TEMPERATURE_QUERY_KEY,
  MANAGERS_QUERY_KEY,
} from '@/services/api/queryKeys';
import type {
  LeadBulkPayload,
  LeadTemperatureListParams,
  LeadTransferredFilter,
} from '@/types/leadTemperature';

const SEARCH_DEBOUNCE_MS = 300;

/** KAN-909: "Все менеджеры" is '' (no manager param); own manager is its id. */
const LEAD_MANAGER_VALUES: ManagerChoiceValues = { all: '', mine: (id) => id };
const LEAD_SPECIAL_MANAGERS = ['', 'none'];

export interface LeadFiltersState {
  levels: LeadTemperatureLevel[];
  toggleLevel: (level: LeadTemperatureLevel) => void;
  manager: string;
  setManager: (manager: string) => void;
  transferred: LeadTransferredFilter;
  setTransferred: (value: LeadTransferredFilter) => void;
  search: string;
  setSearch: (value: string) => void;
  /** KAN-883: day the lead first wrote ("Появился"). */
  leadDay: LeadDayState;
}

/** Filters of the list; every change calls onChange (first page, no selection). */
function useLeadFilters(onChange: () => void): LeadFiltersState & { debouncedSearch: string } {
  const [levels, setLevels] = useState<LeadTemperatureLevel[]>([...LEAD_TEMPERATURE_LEVELS]);
  const { value: manager, choose: chooseManager, clear: clearManager } = useManagerFilterChoice('leads', LEAD_MANAGER_VALUES);
  // Same list as the filter options (LeadFiltersBar): a deleted manager falls back to the default.
  const { data: managers } = useManagers('true');
  useDropUnknownManager(manager, managers, LEAD_SPECIAL_MANAGERS, clearManager);
  const [transferred, setTransferredState] = useState<LeadTransferredFilter>('all');
  const [search, setSearchState] = useState('');
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const leadDay = useLeadDayPeriod(onChange);

  const toggleLevel = useCallback(
    (level: LeadTemperatureLevel) => {
      setLevels((current) => {
        const next = current.includes(level) ? current.filter((l) => l !== level) : [...current, level];
        // The last chip stays on: an empty set would mean "all" on the server.
        if (next.length === 0) return current;
        return LEAD_TEMPERATURE_LEVELS.filter((l) => next.includes(l));
      });
      onChange();
    },
    [onChange]
  );
  const setManager = useCallback(
    (value: string) => { chooseManager(value); onChange(); },
    [chooseManager, onChange]
  );
  const setTransferred = useCallback(
    (value: LeadTransferredFilter) => { setTransferredState(value); onChange(); },
    [onChange]
  );
  const setSearch = useCallback((value: string) => { setSearchState(value); onChange(); }, [onChange]);

  return {
    levels, toggleLevel, manager, setManager, transferred, setTransferred, search, setSearch, leadDay, debouncedSearch,
  };
}

export interface LeadSelection {
  ids: string[];
  isSelected: (id: string) => boolean;
  toggle: (id: string) => void;
  /** Select every id of the page, or clear them when all are selected. */
  togglePage: (pageIds: string[]) => void;
  clear: () => void;
}

function useLeadSelection(): LeadSelection {
  const [ids, setIds] = useState<string[]>([]);
  const isSelected = useCallback((id: string) => ids.includes(id), [ids]);
  const toggle = useCallback((id: string) => {
    setIds((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));
  }, []);
  const togglePage = useCallback((pageIds: string[]) => {
    setIds((current) => {
      const allOn = pageIds.length > 0 && pageIds.every((id) => current.includes(id));
      if (allOn) return current.filter((id) => !pageIds.includes(id));
      return [...current, ...pageIds.filter((id) => !current.includes(id))];
    });
  }, []);
  const clear = useCallback(() => setIds([]), []);
  return { ids, isSelected, toggle, togglePage, clear };
}

export function useLeadTemperaturePage() {
  const queryClient = useQueryClient();
  const paging = usePageSize('leads');
  const { setPage } = paging;
  const selection = useLeadSelection();
  const { clear: clearSelection } = selection;
  // A filter change returns to the first page and drops the selection: the
  // selected rows may not be in the new list. Page changes keep the selection.
  const onFiltersChange = useCallback(() => {
    setPage(1);
    clearSelection();
  }, [setPage, clearSelection]);
  const filters = useLeadFilters(onFiltersChange);

  const params: LeadTemperatureListParams = useMemo(
    () => ({
      levels: filters.levels,
      manager: filters.manager,
      transferred: filters.transferred,
      search: filters.debouncedSearch,
      ...(filters.leadDay.range ? { lead_from: filters.leadDay.range.from, lead_to: filters.leadDay.range.to } : {}),
      page: paging.page,
      page_size: paging.pageSize,
    }),
    [
      filters.levels,
      filters.manager,
      filters.transferred,
      filters.debouncedSearch,
      filters.leadDay.range,
      paging.page,
      paging.pageSize,
    ]
  );

  const list = useQuery({
    queryKey: [...LEAD_TEMPERATURE_QUERY_KEY, params],
    queryFn: () => fetchLeadTemperatureList(params),
    placeholderData: keepPreviousData,
  });

  const bulk = useMutation({
    mutationFn: (payload: LeadBulkPayload) => bulkLeadTemperature(payload),
    onSuccess: (data) => {
      toast.success(`Обновлено: ${data?.updated ?? 0}`);
      selection.clear();
    },
    onError: (err) => toast.error(extractErrorMessage(err, 'Не удалось выполнить действие')),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: LEAD_TEMPERATURE_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ATTENTION_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: MANAGERS_QUERY_KEY });
    },
  });

  const exporter = useMutation({
    mutationFn: (ids: string[]) => exportLeadTemperature(ids),
    onError: (err) => toast.error(extractErrorMessage(err, 'Не удалось выгрузить файл')),
  });

  return { filters, paging, selection, list, bulk, exporter };
}

/** Manual level in the client card: PATCH lead_temperature, then the card is reloaded. */
export function useSetClientLeadTemperature(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (level: string) => clientsApi.update(clientId, { lead_temperature: level }),
    onSuccess: () => {
      void invalidateKeys(queryClient, clientTemperatureChangedKeys(clientId));
    },
  });
}
